import { Component, ElementRef, OnInit, ViewChild, signal } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { feedAnimations } from "app/animations";
import { AuthService } from "app/services/auth/auth.service";
import { webPermisosSignal } from "app/services/auth/web-permisos.signal";
import { EvaluationService } from "app/services/evaluation/evaluation.service";
import { UserService } from "app/services/user/user.service";
import { User } from "app/shared/models/user.interface";
import { DialogObjetivosHistoricoComponent } from "../objetivos-history/objetivos-history.component";

@Component({
    selector: "app-objetivos-departamento",
    templateUrl: "./objetivos-departamento.component.html",
    styleUrls: ["./objetivos-departamento.component.scss"],
    animations: [feedAnimations],
    standalone: false
})
export class ObjetivosDepartamentoComponent implements OnInit {
    readonly permisosPDA = webPermisosSignal(this.authSvc, "WEB-OBJETIVOS");
    readonly user = signal<User | null>(null);
    readonly person = signal<any>(null); // Persona del departamento (diferente del usuario actual para mostrar botones)
    departamentoId: string;
    departamentoNombre: string;

    departamentoAEditar: any = null;

    // Datos del período de revisión de objetivos
    objetivosPeriodRevision: any;
    activo: boolean = false;
    periodo: any;
    evaluandose: string = "";

    // Control de pestañas
    nextSelected: boolean = false;

    // Control de revisión de objetivos
    readonly objetivosRevision = signal<any>(null);

    // Propiedades para objetivos actuales y próximos
    readonly currentPDA = signal<any[]>([]);
    readonly nextPDA = signal<any[]>([]);
    sumaDePesos = 0;
    showHelpBoxActionPlan: boolean = false;
    newActionPlanTitleInput: any;
    readonly mainPeriod = signal<any>(null);
    evPartial: any;

    // Variables para el formulario de nuevo objetivo
    showNewObjetivoForm: boolean = false;
    newObjetivoForm: FormGroup;
    nuevoObjetivo: any = null;

    canWrite: boolean = false;

    readonly loaded = signal(false);

    @ViewChild("titleInput") titleInput: ElementRef;
    @ViewChild("pickerStartDate") pickerStartDate: any;
    @ViewChild("pickerEndDate") pickerEndDate: any;

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private userSvc: UserService,
        private evaluationSvc: EvaluationService,
        private authSvc: AuthService,
        private translate: TranslateService,
        public dialog: MatDialog,
        private formBuilder: FormBuilder
    ) {}

    ngOnInit(): void {
        // Obtener parámetros de la ruta
        this.route.queryParams.subscribe((params) => {
            this.departamentoId = params.departamentoId;

            this.evaluationSvc.getPermisosObjetivos(this.departamentoId).subscribe((data) => {
                this.departamentoAEditar = data;
                this.canWrite = this.departamentoAEditar.canWrite;
                this.loaded.set(true);
                if (this.departamentoAEditar.permisos === true) {
                    this.departamentoNombre = this.departamentoAEditar?.dataDepartamento?.name;
                    // Cargar datos del período de revisión de objetivos
                    this.loadObjetivosPeriodRevision();

                    // Cargar período de revisión de objetivos
                    this.checkObjetivesPeriodRevision();

                    // Inicializar mainPeriod con los datos del período

                    // Inicializar formulario de nuevo objetivo
                    this.initializeNewObjetivoForm();

                    // Cargar objetivos actuales por defecto
                    this.loadCurrentObjetivos();
                }
            });
        });

        // Obtener usuario actual
        this.userSvc.getUser().subscribe((data: User) => {
            this.user.set(data);
            // Crear un objeto person diferente para que aparezcan los botones de editar/eliminar
            this.person.set({ id: -1, displayName: "Departamento" });
        });

        // Cargar permisos para objetivos
    }

    loadObjetivosPeriodRevision() {
        this.evaluationSvc.getObjetivosPeriodRevision().subscribe((data) => {
            this.objetivosPeriodRevision = data;
            this.activo = data?.activo || false;
            this.periodo = data?.periodo;
            this.mainPeriod.set(this.periodo);
            this.evaluandose = data?.evaluandose || "";
        });
    }

    setNextSelected(valor: boolean) {
        this.nextSelected = valor;
        if (valor) {
            this.loadNextPDA();
        } else {
            this.loadCurrentObjetivos();
        }
    }

    loadNextPDA() {
        this.evaluationSvc.getNextObjetivosDepartamentoBlue(this.departamentoId).subscribe((data) => {
            this.nextPDA.set(data ?? []);
            this.checkNextPesos(); // Calcular la suma de pesos
        });
    }

    loadCurrentObjetivos() {
        this.evaluationSvc.getAllObjetivosDepartamento(this.departamentoId).subscribe((data) => {
            this.currentPDA.set(data ?? []);
            this.checkNextPesos(); // Calcular la suma de pesos
        });
    }

    onActionPlanTitle(evt) {
        this.newActionPlanTitleInput = evt.target;
    }

    canCreatePlanAction() {
        return true; // Por defecto permitir crear
    }

    canEditPlanAction(pda) {
        return true; // Por defecto permitir editar
    }

    canBlockPDAs() {
        return false; // Por defecto no permitir bloquear
    }

    canEditObjetivo(pda) {
        return pda.canEdit != null;
    }

    checkObjetivesPeriodRevision() {
        this.evaluationSvc.getObjetivosPeriodRevision().subscribe((data) => {
            if (data?.activo) {
                this.objetivosRevision.set(data.periodo);
            }
        });
    }

    checkNextPesos() {
        let pesos = 0;
        const listaObjetivos = this.nextSelected ? this.nextPDA() : this.currentPDA();

        for (let elem of listaObjetivos) {
            if (Number(elem.peso)) pesos += Number(elem.peso);
        }

        this.sumaDePesos = pesos;

        return pesos <= 100.001 && pesos >= 99.999;
    }

    initializeNewObjetivoForm() {
        this.newObjetivoForm = this.formBuilder.group({
            title: ["", [Validators.required]],
            description: [""],
            peso: [0, [Validators.min(0), Validators.max(100)]],
            startDate: [
                this.periodo ? this.periodo.startPeriod : new Date().toISOString().split("T")[0],
                [Validators.required]
            ],
            endDate: [
                this.periodo ? this.periodo.endPeriod : new Date().toISOString().split("T")[0],
                [Validators.required]
            ]
        });
    }

    addPABlue() {
        // Obtener el valor del input usando ViewChild o la referencia guardada
        let _newActionPlanTitle = "";

        if (this.titleInput && this.titleInput.nativeElement) {
            _newActionPlanTitle = this.titleInput.nativeElement.value;
        } else if (this.newActionPlanTitleInput && this.newActionPlanTitleInput.value) {
            _newActionPlanTitle = this.newActionPlanTitleInput.value;
        }

        // Validar que el título no esté vacío
        if (!_newActionPlanTitle || _newActionPlanTitle.trim() === "") {
            return;
        }

        // Crear el objeto objetivo de departamento para enviar al endpoint
        const objetivoDepartamento = {
            id: null,
            title: _newActionPlanTitle,
            organigrama_id: this.departamentoId,
            peso: 0,
            startDate: this.periodo ? this.periodo.startPeriod : new Date().toISOString().split("T")[0],
            endDate: this.periodo ? this.periodo.endPeriod : new Date().toISOString().split("T")[0],
            isObjetivo: 1
        };

        // Llamar al endpoint para crear el objetivo
        this.evaluationSvc.saveObjetivoDepartamento(objetivoDepartamento).subscribe((data) => {
            if (data.success) {
                this.evaluationSvc.getNextObjetivosDepartamentoBlue(this.departamentoId).subscribe((objetivosData) => {
                    this.nextPDA.set(objetivosData ?? []);
                    if (data.id) {
                        objetivosData.find((ap) => Number(ap.id) == Number(data.id)).isNew = true;
                    }
                    this.checkNextPesos(); // Calcular la suma de pesos
                });
            }
        });

        // Limpiar el input
        if (this.titleInput && this.titleInput.nativeElement) {
            this.titleInput.nativeElement.value = "";
        } else if (this.newActionPlanTitleInput) {
            this.newActionPlanTitleInput.value = "";
        }
    }

    cancelNewObjetivo() {
        this.showNewObjetivoForm = false;
        this.nuevoObjetivo = null;
        this.newObjetivoForm.reset();
        this.initializeNewObjetivoForm();
    }

    getActionsPlans() {
        // Recargar objetivos según el estado actual
        if (this.nextSelected) {
            this.loadNextPDA();
        } else {
            this.loadCurrentObjetivos();
        }
    }

    onChangeElementValue(evt) {
        const _body = {
            id: evt.id,
            result: evt.value
        };
        this.evaluationSvc.saveActionPlanResult(_body).subscribe((data) => {
            console.log(data);
        });
    }

    openHistorioObjetivos() {
        const dialogRef = this.dialog.open(DialogObjetivosHistoricoComponent, {
            data: {
                personId: this.departamentoId, // Usar el ID del departamento en lugar del usuario
                user: this.user(),
                person: this.person(),
                permisosPDA: this.permisosPDA,
                mainPeriod: this.periodo,
                isDepartamento: true, // Indicar que es para objetivos de departamento
                organigramaId: this.departamentoId, // Pasar el ID del organigrama
                close: () => {
                    dialogRef.close();
                }
            },
            panelClass: "panel-feedbalia",
            autoFocus: false,
            maxWidth: "60vw",
            minWidth: "60vw"
        });
    }
}
